import { Button } from '@atd/rui-base.mui.button'
import { IconButton } from '@atd/rui-base.mui.icon-button'
import Tooltip from '@mui/material/Tooltip'

import React from 'react'

// Usage: Generate a row of buttons that automatically handle desktop and mobile sizes
//
// Arguments
// children: information to populate the buttons
//      children should be compatible with the button prototype.
//

// Either a material icon component or the path of an svg icon is expected.
// If both are provided the complete component will take precedence.
// iconPath, mobileIconPath, and disabledIconPath should start inside the public/images directory and exclude the extension
// ex. iconPath = 'jobIcons/Job_SaveBlue' will resolve to
// 'public/images/jobIcons/Job_SaveBlue.svg'
// mobileIconPath & disabledIconPath can be used to override the primary image in the appropriate cases but are not required.

export function buttonPrototype(proto) {
    return Object.assign({
        icon: null,
        iconStyle: null,
        disabledIconStyle: null,
        iconPath: '',
        mobileIconPath: '',
        disabledIconPath: '',
        label: '',
        onClick: () => {},
        href: null,
        disabled: false,
        disabledTooltip: '',
        visible: true,
    }, proto)
}

class ButtonBar extends React.Component {
    static defaultProps = {
        children: [],
        disabled: false, // disables entire bar, overrides individual button states
        mobileFilled: false,
    }
    renderNonMobileChildren = () => {
        const buttons = []
        let i = 0
        for (const buttonInfo of this.props.children) {
            i++
            let icon = buttonInfo.icon
            if (!icon && buttonInfo.disabled && buttonInfo.disabledIconPath) {
                icon = <img className="standardIcon"
                    src={`${process.env.PUBLIC_URL}/images/${buttonInfo.disabledIconPath}.svg`}
                />
            }
            else if (!icon) {
                icon = <img className="standardIcon"
                    src={`${process.env.PUBLIC_URL}/images/${buttonInfo.iconPath}.svg`}
                />
            }
            if (icon && icon.type !== 'img' && !buttonInfo.disabled && !this.props.disabled) {
                icon = React.cloneElement(icon, {htmlColor: 'white'})
            }
            if (buttonInfo.visible) {
                const button = (
                    <Button variant="contained"
                        startIcon={icon}
                        onClick={buttonInfo.onClick}
                        href={buttonInfo.href}
                        disabled={buttonInfo.disabled || this.props.disabled}
                        style={buttonInfo.disabled && buttonInfo.disabledTooltip ? {pointerEvents: 'none'} : undefined}
                        name={`${buttonInfo.label}Button`}
                        color="primary"
                        className="nonMobileButton"
                        key={`button${i}`}>{buttonInfo.label}</Button>
                )
                if (buttonInfo.disabled && buttonInfo.disabledTooltip) {
                    buttons.push(
                        <Tooltip title={buttonInfo.disabledTooltip} key={`button${i}`}>
                            <span style={{display: 'inline-flex'}}>{button}</span>
                        </Tooltip>,
                    )
                }
                else {
                    buttons.push(button)
                }
            }
        }

        return buttons
    }

    renderMobileFilledChildren = () => {
        const buttons = []
        let i = 0
        for (const buttonInfo of this.props.children) {
            i++
            let icon = buttonInfo.icon
            if (!icon && buttonInfo.disabled && buttonInfo.disabledIconPath) {
                icon = <img className="standardIcon"
                    src={`${process.env.PUBLIC_URL}/images/${buttonInfo.disabledIconPath}.svg`}
                />
            }
            else if (!icon) {
                icon = <img className="standardIcon"
                    src={`${process.env.PUBLIC_URL}/images/${buttonInfo.iconPath}.svg`}
                />
            }
            if (icon && icon.type !== 'img' && !buttonInfo.disabled && !this.props.disabled) {
                icon = React.cloneElement(icon, {htmlColor: 'white'})
            }
            if (buttonInfo.visible) {
                const button = (
                    <Button variant="contained"
                        onClick={buttonInfo.onClick}
                        href={buttonInfo.href}
                        disabled={buttonInfo.disabled || this.props.disabled}
                        style={buttonInfo.disabled && buttonInfo.disabledTooltip ? {pointerEvents: 'none'} : undefined}
                        name={`${buttonInfo.label}Button`}
                        color="primary"
                        className="mobileAltButton"
                        aria-label={buttonInfo.label}
                        key={`button${i}`}>{icon}</Button>
                )
                if (buttonInfo.disabled && buttonInfo.disabledTooltip) {
                    buttons.push(
                        <Tooltip title={buttonInfo.disabledTooltip} key={`button${i}`}>
                            <span style={{display: 'inline-flex'}}>{button}</span>
                        </Tooltip>,
                    )
                }
                else {
                    buttons.push(button)
                }
            }
        }

        return buttons
    }

    renderMobileAltChildren = () => {
        if (this.props.mobileFilled) {
            return this.renderMobileFilledChildren()
        }
        const buttons = []
        let i = 0
        for (const buttonInfo of this.props.children) {
            i++
            let icon = buttonInfo.icon

            if (!icon && buttonInfo.disabled && buttonInfo.disabledIconPath) {
                icon = <img className="standardIcon"
                    src={`${process.env.PUBLIC_URL}/images/${buttonInfo.disabledIconPath}.svg`}
                />
            }
            else if (!icon && buttonInfo.mobileIconPath) {
                icon = <img className="standardIcon"
                    src={`${process.env.PUBLIC_URL}/images/${buttonInfo.mobileIconPath}.svg`}
                />
            }
            else if (!icon) {
                icon = <img className="standardIcon"
                    src={`${process.env.PUBLIC_URL}/images/${buttonInfo.iconPath}.svg`}
                />
            }

            let iconStyle = buttonInfo.iconStyle
            if (buttonInfo.disabled && buttonInfo.disabledIconStyle) {
                iconStyle = buttonInfo.disabledIconStyle
            }
            if (icon && iconStyle) {
                icon = React.cloneElement(icon, { style: { ...icon.props.style, ...iconStyle } })
            }
            else if (icon && icon.type !== 'img') {
                icon = React.cloneElement(icon, { htmlColor: buttonInfo.disabled ? '#bdbdbd' : '#555555' })
            }

            if (buttonInfo.visible) {
                buttons.push(
                    <IconButton
                        onClick={buttonInfo.onClick}
                        href={buttonInfo.href}
                        disabled={buttonInfo.disabled}
                        name={`${buttonInfo.label}Button`}
                        className="mobileAltButton"
                        key={`button${i}`}>
                        {icon}
                    </IconButton>,
                )
            }
        }

        return buttons
    }

    render() {
        return (
            <div name="buttonBar">
                <div name="desktopButtons" className="nonMobileButtonGroup">
                    {this.renderNonMobileChildren()}
                </div>
                <div name="mobileButtons" className="mobileAltButtonGroup">
                    {this.renderMobileAltChildren()}
                </div>
            </div>
        )
    }
}

export default ButtonBar
